import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { ReactSVG } from "react-svg";
import github from "../../assets/github.svg";
import apple from "../../assets/apple.svg";
import playStore from "../../assets/google_play_store.svg";

const ProjectSection = ({ project, index, isDark, colors, isReversed }) => {
    const sectionRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    const getIconSrc = (iconName) => {
        switch (iconName) {
            case "github":
                return github;
            case "playStore":
                return playStore;
            case "apple":
                return apple;
            default:
                return github;
        }
    };

    const getIconLabel = (iconName) => {
        switch (iconName) {
            case "playStore":
                return "Play Store";
            case "apple":
                return "App Store";
            case "github":
                return "GitHub";
            default:
                return "View";
        }
    };

    const getProjectSvg = (projectId) => {
        switch(projectId) {
            case 'project1':
                return (
                    <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full opacity-80">
                        <defs>
                            {/* Smooth realistic cylindrical shading */}
                            <linearGradient id="dbGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#d97706" stopOpacity="0.9" />
                                <stop offset="50%" stopColor="#fbbf24" stopOpacity="0.8" />
                                <stop offset="100%" stopColor="#b45309" stopOpacity="0.9" />
                            </linearGradient>
                            <linearGradient id="dbTopGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#fcd34d" stopOpacity="1" />
                                <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.8" />
                            </linearGradient>
                        </defs>
                        {/* Bottom DB Disk - Static Base */}
                        <g>
                            <path d="M 25 70 v 14 a 25 10 0 0 0 50 0 v -14 Z" fill="url(#dbGrad)" stroke="#f97316" strokeWidth="1" />
                            <ellipse cx="50" cy="70" rx="25" ry="10" fill="url(#dbTopGrad)" stroke="#f97316" strokeWidth="1" />
                        </g>
                        {/* Middle DB Disk - Lightly floats */}
                        <g>
                            <animateTransform attributeName="transform" type="translate" values="0,0; 0,-4; 0,0" dur="2.5s" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.4 0 0.6 1; 0.4 0 0.6 1" repeatCount="indefinite" />
                            <path d="M 25 50 v 14 a 25 10 0 0 0 50 0 v -14 Z" fill="url(#dbGrad)" stroke="#f97316" strokeWidth="1" />
                            <ellipse cx="50" cy="50" rx="25" ry="10" fill="url(#dbTopGrad)" stroke="#f97316" strokeWidth="1" />
                        </g>
                        {/* Top DB Disk - Higher separation to indicate reads/writes */}
                        <g>
                            <animateTransform attributeName="transform" type="translate" values="0,0; 0,-8; 0,0" dur="2.5s" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.4 0 0.6 1; 0.4 0 0.6 1" repeatCount="indefinite" />
                            <path d="M 25 30 v 14 a 25 10 0 0 0 50 0 v -14 Z" fill="url(#dbGrad)" stroke="#f97316" strokeWidth="1" />
                            <ellipse cx="50" cy="30" rx="25" ry="10" fill="url(#dbTopGrad)" stroke="#ea580c" strokeWidth="1" />
                            {/* Indicator Light */}
                            <ellipse cx="50" cy="30" rx="4" ry="2" fill="#10b981" className="animate-pulse" />
                        </g>
                    </svg>
                );
            case 'project2':
                return (
                    <svg viewBox="0 0 600 200" className="absolute inset-0 w-full h-full">
                    <defs>
                        <style>
                            {`
                                .logo-container {
                                    will-change: transform, opacity, filter;
                                }
                                
                                /* Increased size requires specific transform origins, changed from 9s to 4.5s for faster switching */
                                .logo-1 { transform-origin: 145px 100px; animation: switch-1 4.5s cubic-bezier(0.5, 0, 0.2, 1.1) infinite; }
                                .logo-2 { transform-origin: 300px 100px; animation: switch-2 4.5s cubic-bezier(0.5, 0, 0.2, 1.1) infinite; }
                                .logo-3 { transform-origin: 455px 100px; animation: switch-3 4.5s cubic-bezier(0.5, 0, 0.2, 1.1) infinite; }

                                /* 
                                Tight 3-phase timing. 
                                Scale reaches 1.25 for massive presence, drops to 0.6 for background elements.
                                */
                                @keyframes switch-1 {
                                    0%, 15%   { transform: scale(1.25); opacity: 1; filter: drop-shadow(0 20px 30px rgba(37, 99, 235, 0.6)); }
                                    33%, 81%  { transform: scale(0.60); opacity: 0.15; filter: drop-shadow(0 0px 0px rgba(37, 99, 235, 0)); }
                                    100%      { transform: scale(1.25); opacity: 1; filter: drop-shadow(0 20px 30px rgba(37, 99, 235, 0.6)); }
                                }

                                @keyframes switch-2 {
                                    0%, 15%   { transform: scale(0.60); opacity: 0.15; filter: drop-shadow(0 0px 0px rgba(236, 72, 153, 0)); }
                                    33%, 48%  { transform: scale(1.25); opacity: 1; filter: drop-shadow(0 20px 30px rgba(225, 29, 72, 0.6)); }
                                    66%, 100% { transform: scale(0.60); opacity: 0.15; filter: drop-shadow(0 0px 0px rgba(236, 72, 153, 0)); }
                                }

                                @keyframes switch-3 {
                                    0%, 48%   { transform: scale(0.60); opacity: 0.15; filter: drop-shadow(0 0px 0px rgba(16, 185, 129, 0)); }
                                    66%, 81%  { transform: scale(1.25); opacity: 1; filter: drop-shadow(0 20px 30px rgba(5, 150, 105, 0.6)); }
                                    100%      { transform: scale(0.60); opacity: 0.15; filter: drop-shadow(0 0px 0px rgba(16, 185, 129, 0)); }
                                }
                            `}
                        </style>

                        {/* --- Logo 1 Gradients (Cool / Blues) --- */}
                        <linearGradient id="cubeTop" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#60A5FA" />
                            <stop offset="100%" stopColor="#2563EB" />
                        </linearGradient>
                        <linearGradient id="cubeRight" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#1E40AF" />
                            <stop offset="100%" stopColor="#0F172A" />
                        </linearGradient>
                        <linearGradient id="cubeLeft" x1="100%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#3B82F6" />
                            <stop offset="100%" stopColor="#1D4ED8" />
                        </linearGradient>

                        {/* --- Logo 2 Gradients (Warm / Magenta & Sunset) --- */}
                        <linearGradient id="venn1" x1="0%" y1="100%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#F43F5E" />
                            <stop offset="100%" stopColor="#BE123C" />
                        </linearGradient>
                        <linearGradient id="venn2" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#F59E0B" />
                            <stop offset="100%" stopColor="#B45309" />
                        </linearGradient>
                        <linearGradient id="venn3" x1="100%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#EC4899" />
                            <stop offset="100%" stopColor="#9D174D" />
                        </linearGradient>

                        {/* --- Logo 3 Gradients (Growth / Greens & Teals) --- */}
                        <linearGradient id="bloom1" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#34D399" />
                            <stop offset="100%" stopColor="#059669" />
                        </linearGradient>
                        <linearGradient id="bloom2" x1="100%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#2DD4BF" />
                            <stop offset="100%" stopColor="#0F766E" />
                        </linearGradient>
                        <linearGradient id="bloom3" x1="0%" y1="100%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#10B981" />
                            <stop offset="100%" stopColor="#047857" />
                        </linearGradient>
                    </defs>

                    {/* ===== LOGO 1: Precision Isometric Tech Cube ===== */}
                    {/* Scaled up massively, repositioned perfectly to X=145 for tighter gaps */}
                    <g className="logo-container logo-1">
                        {/* Top Face */}
                        <path d="M 145,30 L 205.62,65 L 145,100 L 84.38,65 Z" fill="url(#cubeTop)" stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeLinejoin="round" />
                        {/* Right Face */}
                        <path d="M 145,100 L 205.62,65 L 205.62,135 L 145,170 Z" fill="url(#cubeRight)" stroke="rgba(255,255,255,0.05)" strokeWidth="1" strokeLinejoin="round" />
                        {/* Left Face */}
                        <path d="M 145,100 L 84.38,65 L 84.38,135 L 145,170 Z" fill="url(#cubeLeft)" stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeLinejoin="round" />
                        {/* Inner glow structure highlights */}
                        <path d="M 145,98 L 204.62,64.5 M 145,98 L 85.38,64.5 M 145,98 L 145,168" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round" />
                    </g>

                    {/* ===== LOGO 2: Seamless Interlocking Venn Blossom ===== */}
                    {/* Base scale massively increased. Centers tightened */}
                    <g className="logo-container logo-2">
                        <circle cx="276" cy="112" r="45" fill="url(#venn1)" opacity="0.85" style={{ mixBlendMode: 'screen' }} />
                        <circle cx="324" cy="112" r="45" fill="url(#venn2)" opacity="0.85" style={{ mixBlendMode: 'screen' }} />
                        <circle cx="300" cy="74"  r="45" fill="url(#venn3)" opacity="0.85" style={{ mixBlendMode: 'screen' }} />
                        {/* Ultra-bright glowing intersection core */}
                        <circle cx="300" cy="99" r="16" fill="#ffffff" opacity="0.25" filter="blur(4px)" />
                        <circle cx="300" cy="99" r="4" fill="#ffffff" opacity="0.8" />
                    </g>

                    {/* ===== LOGO 3: Geometric Overlapping Bloom Matrix ===== */}
                    {/* Increased base rect size to match mass of others, tightened X gap to 455 */}
                    <g className="logo-container logo-3">
                        <rect x="412" y="57" width="86" height="86" rx="28" transform="rotate(0 455 100)"   fill="url(#bloom1)" opacity="0.85" />
                        <rect x="412" y="57" width="86" height="86" rx="28" transform="rotate(30 455 100)"  fill="url(#bloom2)" opacity="0.85" />
                        <rect x="412" y="57" width="86" height="86" rx="28" transform="rotate(60 455 100)"  fill="url(#bloom3)" opacity="0.85" />
                        <circle cx="455" cy="100" r="18" fill="#022C22" opacity="0.6" />
                        <circle cx="455" cy="100" r="8" fill="#10B981" />
                    </g>
                </svg>
                );
            case 'project3':
                return (
                    <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full opacity-80">
                        {/* Clean Network Connecting Paths */}
                        <g stroke="#9ca3af" strokeWidth="1.5" opacity="0.3" fill="none">
                            <line x1="25" y1="25" x2="50" y2="50" />
                            <line x1="75" y1="25" x2="50" y2="50" />
                            <line x1="50" y1="80" x2="50" y2="50" />
                        </g>
    
                        {/* Animated Data Packets Flowing - same duration so both top boxes emit in sync */}
                        <circle r="2.5" fill="#f59e0b" opacity="0.9">
                            <animateMotion path="M 25 25 L 50 50" dur="1.5s" repeatCount="indefinite" begin="0s" />
                        </circle>
                        <circle r="2.5" fill="#10b981" opacity="0.9">
                            <animateMotion path="M 75 25 L 50 50" dur="1.5s" repeatCount="indefinite" begin="0s" />
                        </circle>
                        <circle r="2.5" fill="#3b82f6" opacity="0.9">
                            <animateMotion path="M 50 50 L 50 80" dur="1.5s" repeatCount="indefinite" begin="1.5s" />
                        </circle>
                        
                        {/* Central DB/API Processing Node */}
                        <rect x="40" y="40" width="20" height="20" rx="5" fill="#ef4444" opacity="0.8" className="animate-pulse" />
                        {/* <rect x="42" y="42" width="16" height="16" rx="3" fill="none" stroke="#ef4444" strokeWidth="1.5" className="animate-ping" style={{ animationDuration: '3s' }} /> */}
                        <circle cx="50" cy="50" r="2" fill="#fff" />
    
                        {/* Edge Nodes (Clients, Database, Front-end) */}
                        <rect x="19" y="19" width="12" height="12" rx="3" fill="#f59e0b" />
                        <rect x="69" y="19" width="12" height="12" rx="3" fill="#10b981" />
                        <rect x="44" y="74" width="12" height="12" rx="3" fill="#3b82f6" />
                    </svg>
                );
            case 'project4':
                return (
                    <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full opacity-80">
                        <defs>
                            <linearGradient id="droneGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.8" />
                                <stop offset="100%" stopColor="#b45309" stopOpacity="0.4" />
                            </linearGradient>
                        </defs>
                        <rect x="40" y="40" width="20" height="20" rx="4" fill="url(#droneGrad)" stroke="#f59e0b" strokeWidth="1.5" className="animate-pulse" />
                        <line x1="40" y1="40" x2="25" y2="25" stroke="currentColor" strokeWidth="2" opacity="0.6" />
                        <line x1="60" y1="40" x2="75" y2="25" stroke="currentColor" strokeWidth="2" opacity="0.6" />
                        <line x1="40" y1="60" x2="25" y2="75" stroke="currentColor" strokeWidth="2" opacity="0.6" />
                        <line x1="60" y1="60" x2="75" y2="75" stroke="currentColor" strokeWidth="2" opacity="0.6" />
                        <g className="animate-[spin_1s_linear_infinite]" style={{ transformOrigin: '25px 25px' }}>
                            <ellipse cx="25" cy="25" rx="10" ry="2" fill="#10b981" opacity="0.8" />
                            <ellipse cx="25" cy="25" rx="2" ry="10" fill="#10b981" opacity="0.8" />
                        </g>
                        <g className="animate-[spin_1s_linear_infinite_reverse]" style={{ transformOrigin: '75px 25px' }}>
                            <ellipse cx="75" cy="25" rx="10" ry="2" fill="#ef4444" opacity="0.8" />
                            <ellipse cx="75" cy="25" rx="2" ry="10" fill="#ef4444" opacity="0.8" />
                        </g>
                        <g className="animate-[spin_1s_linear_infinite_reverse]" style={{ transformOrigin: '25px 75px' }}>
                            <ellipse cx="25" cy="75" rx="10" ry="2" fill="#ef4444" opacity="0.8" />
                            <ellipse cx="25" cy="75" rx="2" ry="10" fill="#ef4444" opacity="0.8" />
                        </g>
                        <g className="animate-[spin_1s_linear_infinite]" style={{ transformOrigin: '75px 75px' }}>
                            <ellipse cx="75" cy="75" rx="10" ry="2" fill="#10b981" opacity="0.8" />
                            <ellipse cx="75" cy="75" rx="2" ry="10" fill="#10b981" opacity="0.8" />
                        </g>
                        {/* Drone center indicator (circles moving behind drone REMOVED entirely) */}
                        <circle cx="50" cy="50" r="3" fill="#10b981" />
                    </svg>
                );
            default:
                return (
                    <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full opacity-80">
                        {/* Very solid geometric core removing the arbitrary dashed concentric circles */}
                        {/* Outer Hexagon Ping Layer */}
                        <polygon points="50,15 80,32 80,68 50,85 20,68 20,32" fill="none" stroke="#f59e0b" strokeWidth="1" opacity="0.4" className="animate-[ping_4s_ease-in-out_infinite]" />
                        
                        {/* Solid Hexagon Base */}
                        <polygon points="50,15 80,32 80,68 50,85 20,68 20,32" fill="none" stroke="#10b981" strokeWidth="2" opacity="0.5" />
                        
                        {/* 3D Isometric Intersecting Inner Structure */}
                        <polyline points="20,32 50,50 80,32" fill="none" stroke="#10b981" strokeWidth="1.5" opacity="0.5" />
                        <line x1="50" y1="50" x2="50" y2="85" stroke="#10b981" strokeWidth="1.5" opacity="0.5" />
                        
                        {/* Inner Cube Outline */}
                        <polygon points="50,30 65,40 65,60 50,70 35,60 35,40" fill="none" stroke="#ef4444" strokeWidth="1.5" opacity="0.6" className="animate-pulse" />
                        
                        {/* Power Core Node */}
                        <circle cx="50" cy="50" r="3" fill="#ef4444" className="animate-pulse" />
                    </svg>
                );
        }
    };

    return (
        <div 
            ref={sectionRef}
            className={`flex flex-col-reverse ${isReversed ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-12 md:gap-24 min-h-[60vh] py-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
        >
            {/* Text Content */}
            <div className="flex-1 space-y-6 text-center md:text-left z-10 relative">
                <h3 className={`text-5xl md:text-7xl font-semibold tracking-tight ${colors.text}`}>
                    {project.name}
                </h3>
                {project.extra_info && (
                    <p className={`text-xl font-medium bg-gradient-to-r from-emerald-500 to-amber-500 bg-clip-text text-transparent`}>
                        {project.extra_info}
                    </p>
                )}
                <p className={`text-xl md:text-2xl font-light leading-relaxed ${colors.textSecondary}`}>
                    {project.description}
                </p>

                {/* Action links */}
                {project.links && project.links.length > 0 && (
                    <div className="flex flex-wrap gap-4 justify-center md:justify-start pt-6">
                        {project.links.map((link, linkIndex) => {
                            const isStoreLink = link.icon === 'playStore' || link.icon === 'apple';
                            return (
                                <a
                                    key={linkIndex}
                                    href={link.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={isStoreLink 
                                        ? "relative inline-flex items-center gap-3 px-8 py-4 font-medium rounded-xl overflow-hidden transition-all duration-400 bg-emerald-600 text-white hover:bg-emerald-500 hover:-translate-y-1 hover:shadow-[0_10px_40px_-10px_rgba(16,185,129,0.5)]"
                                        : "glass-button !px-8 !py-4 !rounded-xl"
                                    }
                                >
                                    {link.icon === 'playStore' ? (
                                        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                                        </svg>
                                    ) : link.icon === 'apple' ? (
                                        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z"/>
                                        </svg>
                                    ) : (
                                        <ReactSVG
                                            src={getIconSrc(link.icon)}
                                            className={`w-6 h-6 [&_svg]:w-6 [&_svg]:h-6 ${isDark ? '[&_path]:fill-white' : '[&_path]:fill-black'}`}
                                        />
                                    )}
                                    <span className="whitespace-nowrap font-medium text-lg">
                                        {getIconLabel(link.icon)}
                                    </span>
                                </a>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Visual/Image Content */}
            <div className={`flex-1 w-full flex justify-center relative group transition-transform duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-24 opacity-0'}`} style={{ transitionDelay: '200ms' }}>
                {/* Parallax Float Container */}
                <div className="relative animate-[float_8s_ease-in-out_infinite]">
                    {/* Glowing Backdrop */}
                    <div className={`absolute inset-0 bg-gradient-to-tr from-emerald-500/20 to-amber-500/20 blur-[80px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-1000 scale-150 -z-10`}></div>
                    
                    {project.img ? (
                        <div className="relative">
                            <img
                                src={`/assets/${project.img}`}
                                alt={project.name}
                                className={`w-64 h-64 md:w-96 md:h-96 object-contain transition-all duration-700 group-hover:scale-110 group-hover:rotate-2 drop-shadow-2xl z-10`}
                            />
                            {/* Glass overlay reflection for the image */}
                            <div className="absolute inset-0 rounded-[3rem] pointer-events-none bg-gradient-to-br from-white/20 to-transparent mix-blend-overlay"></div>
                        </div>
                    ) : (
                        <div className={`w-64 h-64 md:w-96 md:h-96 rounded-[3rem] flex items-center justify-center transition-all duration-700 group-hover:scale-105 shadow-2xl z-10 backdrop-blur-3xl overflow-hidden relative ${isDark ? 'bg-white/5 border border-white/10 shadow-[0_0_40px_rgba(255,255,255,0.05)]' : 'bg-black/5 border border-black/10 shadow-[0_0_40px_rgba(0,0,0,0.05)]'}`}>
                            {getProjectSvg(project.id)}
                            <div className="absolute inset-0 rounded-[3rem] pointer-events-none bg-gradient-to-br from-white/40 to-transparent mix-blend-overlay"></div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ProjectLayout = ({ data }) => {
    const { colors, isDark } = useTheme();
    const sectionRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    const projects = Object.entries(data).map(([key, value]) => ({
        id: key,
        ...value,
    }));

    return (
        <section 
            ref={sectionRef}
            className="relative py-32 px-6 overflow-hidden"
        >
            <div className="relative max-w-7xl mx-auto space-y-24">
                <div 
                    className={`text-center mb-32 transition-all duration-1000 ${
                        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                    }`}
                >
                    <h2 className={`heading-lg ${colors.text} mb-6 tracking-tight text-glow`}>
                        PROJECTS
                    </h2>
                    <p className={`body-lg ${colors.textSecondary} max-w-2xl mx-auto font-light`}>
                    <span className="block text-amber-500 font-medium mb-3 uppercase tracking-widest text-sm drop-shadow-md">Award-Winning Excellence</span>
                        A selection of tools and applications built with care and precision, focusing on seamless user experiences.
                    </p>
                </div>

                <div className="space-y-40">
                    {projects.map((project, index) => (
                        <ProjectSection
                            key={project.id}
                            project={project}
                            index={index}
                            isDark={isDark}
                            colors={colors}
                            isReversed={index % 2 !== 0} // Alternate sides
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ProjectLayout;
